// Map veto page (/veto/:roomId; also embedded in the public match page's overlay).
// Layout "map board" (styles in veto/veto.css): teams header with the acting team lit in its colour,
// one sentence for what happens now, the step sequence, the map pool as posters (ban slash / pick
// frame in the acting team's colour), then roster | log | roster. After the veto the connect
// commands sit at the top. Team 1 = blue, team 2 = orange everywhere on the page.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import './veto/veto.css';

const shortMap = (name) => String(name || '').replace(/^(de_|cs_)/, '');
const mapTitle = (name) => shortMap(name).replace(/(^|_)(\w)/g, (_, s, c) => `${s ? ' ' : ''}${c.toUpperCase()}`);

// map image: try .jpg when .png is missing (and back), then hide
const imgFallback = (e) => {
  const el = e.target;
  if (el.dataset.tried) { el.style.display = 'none'; return; }
  el.dataset.tried = '1';
  if (el.src.endsWith('.png')) el.src = el.src.replace(/\.png$/, '.jpg');
  else if (el.src.endsWith('.jpg')) el.src = el.src.replace(/\.jpg$/, '.png');
  else el.style.display = 'none';
};

// inline icons (the app doesn't load an icon font)
const Svg = ({ d, size = 14, sw = 3 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={d} /></svg>
);
const CHECK = 'M20 6L9 17l-5-5';
const CROSS = 'M18 6L6 18M6 6l12 12';

function RotatingStatusText() {
  const { t } = useTranslation();
  const msgs = [
    t('veto.setupMaps', 'Loading the maps…'),
    t('veto.setupAvatars', 'Setting up player avatars…'),
    t('veto.setupNames', 'Setting up player names…'),
    t('veto.setupServer', 'Configuring the server…'),
  ];
  const [index, setIndex] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setIndex(i => (i + 1) % msgs.length), 6000);
    return () => clearInterval(id);
  }, [msgs.length]);
  return <div key={index} className="vt-rot">{msgs[index]}</div>;
}

function FaceitBadge({ faceit }) {
  if (!faceit) return null;
  const hasLevel = faceit.skill_level !== null && faceit.skill_level !== undefined;
  const hasElo = faceit.faceit_elo !== null && faceit.faceit_elo !== undefined;
  if (!hasLevel && !hasElo) return null;
  const level = hasLevel ? faceit.skill_level : '?';
  const n = Number(level);
  const iconLevel = Number.isInteger(n) && n >= 1 && n <= 10 ? n : null;
  const elo = hasElo ? Math.round(Number(faceit.faceit_elo)) : null;
  const title = `FACEIT${faceit.nickname ? ` ${faceit.nickname}` : ''}: Level ${level}${elo !== null ? `, ${elo} ELO` : ''}`;
  return (
    <span className="vt-faceit" title={title}>
      {iconLevel ? <img src={`/faceit-levels/skill_level_${iconLevel}.png`} alt={`FACEIT Level ${iconLevel}`} /> : <span>F{level}</span>}
      {elo !== null && elo}
    </span>
  );
}

export default function Veto({ roomId: roomIdProp = null, embedded = false, onClose = null } = {}) {
  const { roomId: roomIdParam } = useParams();
  const navigate = useNavigate();
  // When embedded (e.g. rendered inside a modal on the public match page), the
  // roomId comes in as a prop instead of a route param.
  const roomId = roomIdProp || roomIdParam;
  const [searchParams] = useSearchParams();
  const key = searchParams.get('key') || '';
  const { t } = useTranslation();

  // "View Match" navigation. Embedded (overlay on the match page): just close the
  // overlay — the match page is already behind it. Standalone /veto/:id page:
  // navigate to the public match page.
  const goToMatch = () => {
    if (embedded) {
      if (onClose) onClose();
      return;
    }
    if (session && session.match_id) navigate(`/match/${session.match_id}`);
  };

  const [session, setSession] = useState(null);
  const [role, setRole] = useState('viewer');
  const [teamNames, setTeamNames] = useState({ team1: 'Team 1', team2: 'Team 2' });
  const [teamLogos, setTeamLogos] = useState({ team1: null, team2: null });
  const [teamPlayers, setTeamPlayers] = useState({ team1: [], team2: [] });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [sidePickMap, setSidePickMap] = useState(null);
  const [coinAnimating, setCoinAnimating] = useState(false);
  const [coinRevealed, setCoinRevealed] = useState(false);

  const [serverInfo, setServerInfo] = useState(null);
  const [matchStatus, setMatchStatus] = useState(null);
  const [copied, setCopied] = useState(null);
  const [rosterPage, setRosterPage] = useState({ team1: 0, team2: 0 });
  const [serverModalDismissed, setServerModalDismissed] = useState(false);
  const [setupStepLabel, setSetupStepLabel] = useState('');
  const [startMatchStep, setStartMatchStep] = useState(0);
  const [setupHasBackup, setSetupHasBackup] = useState(true); // false = no SFTP/FTP: avatars + names are skipped
  const [setupLastStep, setSetupLastStep] = useState(0); // last step that started (a failure is shown on it)
  const matchIdRef = useRef(null);

  // Steam authentication state
  const [steamAuthRequired, setSteamAuthRequired] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authenticatedUsers, setAuthenticatedUsers] = useState({ A: [], B: [] });
  const [steamLoginLoading, setSteamLoginLoading] = useState(false);

  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const wsMountedRef = useRef(true);
  const prevCoinStatusRef = useRef(null);
  const prevStepRef = useRef(null);

  const copyToClipboard = useCallback((text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(null), 2000);
  }, []);

  // Fetch server info via HTTP and set it in state (called when setup completes or on page load)
  const fetchAndSetServerInfo = useCallback(() => {
    axios.get(`/api/veto/${roomId}?key=${key}`).then(r => {
      if (r.data?.server_ip) {
        setServerInfo({
          ip: r.data.server_ip, port: r.data.server_port,
          public_ip: r.data.server_public_ip || null,
          password: r.data.server_password || null,
          sdr: r.data.sdr_address || null,
          sdrEnabled: !!r.data.sdr_enabled,
        });
        if (r.data.match_status) setMatchStatus(r.data.match_status);
      }
    }).catch(() => { });
  }, [roomId, key]);

  const fetchSession = useCallback(async () => {
    try {
      const res = await axios.get(`/api/veto/${roomId}?key=${key}`);
      setSession(res.data);
      matchIdRef.current = res.data.match_id || null;
      setRole(res.data.role);
      setTeamNames({ team1: res.data.team1_name, team2: res.data.team2_name });
      setTeamLogos({ team1: res.data.team1_logo, team2: res.data.team2_logo });
      setTeamPlayers({ team1: res.data.team1_players || [], team2: res.data.team2_players || [] });

      // Steam authentication
      setSteamAuthRequired(!!res.data.steam_login_required);
      setAuthenticatedUsers(res.data.authenticated_users || { A: [], B: [] });
      const myRole = res.data.role;
      if (myRole === 'A' || myRole === 'B') {
        const authUsers = res.data.authenticated_users || { A: [], B: [] };
        setIsAuthenticated(authUsers[myRole] && authUsers[myRole].length > 0);
      } else {
        setIsAuthenticated(false); // Admin/viewer don't need auth
      }

      const cf = res.data.coin_flip;
      if (cf && (cf.status === 'flipped' || cf.status === 'decided')) {
        prevCoinStatusRef.current = cf.status;
        setCoinRevealed(true);
      }
      if (res.data.finished && res.data.server_ip) {
        setMatchStatus(res.data.match_status || null);
        // Veto already finished + server info available → setup is done, show directly
        setServerInfo({ ip: res.data.server_ip, port: res.data.server_port, public_ip: res.data.server_public_ip || null, password: res.data.server_password || null, sdr: res.data.sdr_address || null, sdrEnabled: !!res.data.sdr_enabled });
        setStartMatchStep(8); // Mark setup as complete
      }
      // NOTE: Do NOT set prevStepRef.current here — let the side-pick effect detect initial load
      setLoading(false);
    } catch (e) {
      setError(e.response?.data?.error || t('veto.loadFailed', 'Could not load this veto'));
      setLoading(false);
    }
  }, [roomId, key]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle Steam login
  const handleSteamLogin = useCallback(async () => {
    try {
      setSteamLoginLoading(true);
      const res = await axios.get(`/api/veto/${roomId}/steam-login-url?key=${key}`);
      if (res.data?.url) {
        // Redirect to Steam OpenID
        window.location.href = res.data.url;
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to get Steam login URL');
      setSteamLoginLoading(false);
    }
  }, [roomId, key]);

  useEffect(() => { fetchSession(); }, [fetchSession]);

  useEffect(() => {
    const connect = () => {
      const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
      // Pass roomId + key so the server can resolve our role and decide whether to send
      // admin/team-only data (e.g. sv_password in the veto_finished message).
      const ws = new WebSocket(`${proto}://${window.location.host}/ws/public?roomId=${encodeURIComponent(roomId)}&key=${encodeURIComponent(key || '')}`);
      wsRef.current = ws;
      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.type === 'veto_update' && msg.data?.session?.id === roomId) {
            setSession(prev => ({ ...prev, ...msg.data.session, role: prev?.role }));
            if (msg.data.session.finished && msg.data.session.match_status) {
              setMatchStatus(msg.data.session.match_status);
            }
          }
          if (msg.type === 'veto_finished') {
            setSession(prev => prev ? { ...prev, finished: true } : prev);
            // Don't show IP yet — wait for 8-step setup to complete.
            // Set a fallback timeout in case WS progress messages are lost.
            setTimeout(() => {
              setStartMatchStep(prev => {
                if (prev < 8 && prev !== -1) {
                  // Fallback: force-fetch server info after 120s
                  fetchAndSetServerInfo();
                  return 8;
                }
                return prev;
              });
            }, 120000);
          }
          // every match's setup is broadcast on /ws/public: only this veto's match counts
          if (msg.type === 'match_setup_progress' && msg.data && (msg.data.match_id == null || matchIdRef.current == null || Number(msg.data.match_id) === Number(matchIdRef.current))) {
            if (msg.data.step !== undefined) {
              setStartMatchStep(msg.data.step);
              if (msg.data.step > 0) setSetupLastStep(msg.data.step);
            }
            if (msg.data.label) setSetupStepLabel(msg.data.label);
            if (msg.data.has_backup !== undefined) setSetupHasBackup(!!msg.data.has_backup);
            // When all 8 steps complete, fetch full server info (IP, password, SDR)
            if (msg.data.step >= 8) {
              fetchAndSetServerInfo();
            }
          }
        } catch (_) { }
      };
      ws.onclose = () => {
        wsRef.current = null;
        // Only reconnect if still mounted — otherwise close() during cleanup would
        // schedule a zombie socket that reconnects forever after unmount.
        if (wsMountedRef.current) reconnectRef.current = setTimeout(connect, 3000);
      };
    };
    wsMountedRef.current = true;
    connect();
    return () => {
      wsMountedRef.current = false;
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (wsRef.current) wsRef.current.close();
    };
  }, [roomId, key, fetchAndSetServerInfo]);

  // SDR poll — the SDR relay address is captured a few seconds AFTER the veto finishes
  // (once the match loads and the map spins up), so it's usually NULL on the first fetch.
  // While the flag is on and we don't yet have it, re-GET the session until it arrives.
  // Admin/A/B only (viewers never receive sdr_enabled), capped so it can't poll forever.
  useEffect(() => {
    if (!session?.finished || role === 'viewer') return;
    if (!serverInfo?.sdrEnabled || serverInfo?.sdr) return;
    let attempts = 0;
    const maxAttempts = 30; // ~1.5 min at 3s cadence
    const poll = () => {
      attempts += 1;
      if (attempts > maxAttempts) { clearInterval(id); return; }
      axios.get(`/api/veto/${roomId}?key=${key}`).then(r => {
        if (r.data?.sdr_address) {
          setServerInfo(prev => prev ? { ...prev, sdr: r.data.sdr_address } : prev);
          clearInterval(id);
        }
      }).catch(() => { });
    };
    poll(); // fire immediately - don't wait for the first interval tick
    const id = setInterval(poll, 3000);
    return () => clearInterval(id);
  }, [session?.finished, role, serverInfo?.sdrEnabled, serverInfo?.sdr, roomId, key]);

  // Coin flip animation trigger
  useEffect(() => {
    if (!session) return;
    const cf = session.coin_flip && typeof session.coin_flip === 'object'
      ? session.coin_flip
      : (session.coin_flip ? JSON.parse(session.coin_flip) : null);
    const newStatus = cf?.status || null;
    const oldStatus = prevCoinStatusRef.current;
    if (newStatus === 'flipped' && oldStatus !== 'flipped' && oldStatus !== 'decided') {
      setCoinAnimating(true);
      setCoinRevealed(false);
      setTimeout(() => { setCoinAnimating(false); setCoinRevealed(true); }, 3000);
    }
    prevCoinStatusRef.current = newStatus;
  }, [session]);

  // Auto-open side pick modal (on step change AND on initial load/refresh)
  useEffect(() => {
    if (!session || session.finished) return;
    const seq = Array.isArray(session.sequence) ? session.sequence : JSON.parse(session.sequence || '[]');
    const currentStep = seq[session.step] || null;
    if (!currentStep || currentStep.a !== 'side') {
      prevStepRef.current = session.step;
      return;
    }

    const isInitialLoad = prevStepRef.current === null;
    const stepChanged = session.step !== prevStepRef.current;

    // Open modal on initial load (refresh) or when step changes to a side step
    if (isInitialLoad || stepChanged) {
      const isMyTurn = (role === currentStep.t || role === 'admin');
      if (isMyTurn) {
        const playedMaps = Array.isArray(session.played_maps) ? session.played_maps : JSON.parse(session.played_maps || '[]');
        const mapsArr = Array.isArray(session.maps) ? session.maps : JSON.parse(session.maps || '[]');
        const seqArr = Array.isArray(session.sequence) ? session.sequence : JSON.parse(session.sequence || '[]');
        const prevStep = session.step > 0 ? seqArr[session.step - 1] : null;
        const prevWasPick = prevStep && prevStep.a === 'pick';
        const targetMap = prevWasPick && playedMaps.length > 0
          ? playedMaps[playedMaps.length - 1]
          : mapsArr.find(m => m.status === 'available')?.name;
        if (targetMap) setSidePickMap(targetMap);
      }
    }
    prevStepRef.current = session.step;
  }, [session, role]);

  const flash = (msg) => { setError(msg); setTimeout(() => setError(null), 3000); };

  const handleAction = async (mapName, side) => {
    if (actionLoading) return;

    // Check Steam authentication if required
    if (steamAuthRequired && (role === 'A' || role === 'B') && !isAuthenticated) {
      flash(t('veto.steamNeeded', 'Log in with Steam to take part in the veto.'));
      return;
    }

    setActionLoading(true);
    try {
      await axios.post(`/api/veto/${roomId}/action`, { key, map: mapName, side });
      setSidePickMap(null);
    } catch (e) {
      flash(e.response?.data?.error || 'Action failed');
    }
    setActionLoading(false);
  };

  const handleReady = async () => {
    try { await axios.post(`/api/veto/${roomId}/ready`, { key }); }
    catch (e) { setError(e.response?.data?.error || 'Ready failed'); }
  };

  const handleCoinCall = async (call) => {
    try { await axios.post(`/api/veto/${roomId}/coin-call`, { key, call }); }
    catch (e) { setError(e.response?.data?.error || 'Coin flip failed'); }
  };

  const handleCoinDecision = async (decision) => {
    try { await axios.post(`/api/veto/${roomId}/coin-decision`, { key, decision }); }
    catch (e) { setError(e.response?.data?.error || 'Decision failed'); }
  };

  const handleReset = () => {
    if (!window.confirm(t('veto.resetConfirm', 'Reset the veto? Every ban, pick and side is cleared and the veto starts again from the first step. This cannot be undone.'))) return;
    setActionLoading(true);
    axios.post(`/api/veto/${roomId}/reset`, { key })
      .then(() => setActionLoading(false))
      .catch(e => { setError(e.response?.data?.error || 'Reset failed'); setActionLoading(false); });
  };

  // --- RENDER ---
  const shell = (children, cls = '') => <div className={`vt${embedded ? ' embedded' : ''}`}><div className={`vt-center ${cls}`}>{children}</div></div>;
  if (loading) return shell(t('veto.loading', 'Loading the veto…'));
  if (error && !session) return shell(error, 'err');
  if (!session) return null;

  const mapsRaw = Array.isArray(session.maps) ? session.maps : JSON.parse(session.maps || '[]');
  const sequence = Array.isArray(session.sequence) ? session.sequence : JSON.parse(session.sequence || '[]');
  const logs = Array.isArray(session.logs) ? session.logs : JSON.parse(session.logs || '[]');
  const logText = (log) => (typeof log === 'string' ? log : (log?.message || ''));

  // When veto is finished: the played maps first in play order (Map 1, 2, 3), then the bans in the
  // order they happened
  const playedOrder = Array.isArray(session.played_maps) ? session.played_maps : JSON.parse(session.played_maps || '[]');
  const maps = (() => {
    if (!session.finished) return mapsRaw;
    const played = playedOrder.map(n => mapsRaw.find(m => m.name === n)).filter(Boolean);
    const ordered = [...played];
    const used = new Set(played.map(m => m.name));
    for (const log of logs) {
      const lower = logText(log).toLowerCase();
      if (!lower) continue;
      for (const m of mapsRaw) {
        if (used.has(m.name)) continue;
        if (lower.includes(shortMap(m.name).toLowerCase())) { ordered.push(m); used.add(m.name); break; }
      }
    }
    for (const m of mapsRaw) if (!used.has(m.name)) ordered.push(m);
    return ordered;
  })();
  const ready = typeof session.ready === 'object' ? session.ready : JSON.parse(session.ready || '{}');
  const coinFlip = session.coin_flip && typeof session.coin_flip === 'object' ? session.coin_flip : (session.coin_flip ? JSON.parse(session.coin_flip) : null);
  const currentStep = sequence[session.step] || null;
  const vetoStarted = !session.use_coin_flip || (ready.A && ready.B && coinFlip && coinFlip.status === 'decided');
  const isMyTurn = currentStep && !session.finished && vetoStarted && (role === currentStep.t || role === 'admin');
  const teamA = session.team_a === 'team1' ? teamNames.team1 : teamNames.team2;
  const teamB = session.team_a === 'team1' ? teamNames.team2 : teamNames.team1;

  const getTeamName = (who) => who === 'A' ? teamA : who === 'B' ? teamB : t('veto.system', 'Decider');
  // A/B (veto roles) → the screen side's colour class: team 1 = t1 (blue), team 2 = t2 (orange)
  const colorOf = (who) => who === 'A' ? (session.team_a === 'team1' ? 't1' : 't2') : who === 'B' ? (session.team_a === 'team1' ? 't2' : 't1') : 'sys';
  const roleTeamKey = role === 'A' || role === 'B' ? (colorOf(role) === 't1' ? 'team1' : 'team2') : null;

  // who starts the veto (shown next to that team's name once it began)
  const startsVetoKey = vetoStarted && !session.finished && sequence.length ? (colorOf(sequence[0].t) === 't1' ? 'team1' : 'team2') : null;
  const actingKey = currentStep && !session.finished && vetoStarted && currentStep.t !== 'System' ? (colorOf(currentStep.t) === 't1' ? 'team1' : 'team2') : null;

  // how many of the same action the acting team does in a row
  let runCount = 0;
  if (currentStep) {
    for (let i = session.step; i < sequence.length; i++) {
      if (sequence[i].t === currentStep.t && sequence[i].a === currentStep.a) runCount++; else break;
    }
  }

  // the sentence for "what happens now"
  let headline = '', sub = '', headColor = null;
  if (session.finished) {
    headline = t('veto.complete', 'Veto complete');
    sub = t('veto.completeSub', 'The maps are played in the order shown, followed by the banned maps.');
  } else if (session.use_coin_flip && (!coinFlip || coinFlip.status !== 'decided')) {
    if (!ready.A || !ready.B) { headline = t('veto.readyTitle', 'Waiting for both teams to ready up'); }
    else if (!coinFlip) headline = role === 'A' ? t('veto.callCoin', 'Call the coin flip') : t('veto.waitingCoin', 'Waiting for the coin flip');
    else if (coinFlip.status === 'flipped' && !coinRevealed) headline = t('veto.flipping', 'Flipping the coin…');
    else if (coinFlip.status === 'flipped') {
      headline = role === coinFlip.winner ? t('veto.wonCoin', 'You won the coin flip: choose who starts the veto') : t('veto.otherWonCoin', '{{team}} won the coin flip', { team: getTeamName(coinFlip.winner) });
      headColor = `var(--vt-${colorOf(coinFlip.winner)})`;
    }
  } else if (currentStep) {
    const actor = getTeamName(currentStep.t);
    const mine = isMyTurn && role !== 'admin';
    const one = runCount === 1;
    const lines = {
      ban: mine
        ? (one ? t('veto.yourBanOne', 'Your turn: ban a map') : t('veto.yourBan', 'Your turn: ban {{count}} maps', { count: runCount }))
        : (one ? t('veto.theyBanOne', '{{team}} is banning a map', { team: actor }) : t('veto.theyBan', '{{team}} is banning {{count}} maps', { team: actor, count: runCount })),
      pick: mine
        ? (one ? t('veto.yourPickOne', 'Your turn: pick a map') : t('veto.yourPick', 'Your turn: pick {{count}} maps', { count: runCount }))
        : (one ? t('veto.theyPickOne', '{{team}} is picking a map', { team: actor }) : t('veto.theyPick', '{{team}} is picking {{count}} maps', { team: actor, count: runCount })),
      side: mine ? t('veto.yourSide', 'Your turn: choose your starting side') : t('veto.theySide', '{{team}} is choosing a starting side', { team: actor }),
      knife: t('veto.knifeStep', 'The last map is the decider, with a knife round for sides'),
    };
    headline = lines[currentStep.a] || '';
    headColor = currentStep.t === 'System' ? null : `var(--vt-${colorOf(currentStep.t)})`;
    sub = role === 'admin' ? t('veto.adminActs', 'As admin you can act for either team.') : '';
  }

  const mapNumber = {};
  playedOrder.forEach((name, idx) => { mapNumber[name] = idx + 1; });

  const stepName = (a) => ({ ban: t('veto.actBan', 'Ban'), pick: t('veto.actPick', 'Pick'), side: t('veto.actSide', 'Side'), knife: t('veto.actKnife', 'Decider') }[a] || a);

  const logKind = (log) => {
    const lower = logText(log).toLowerCase();
    if (lower.includes('removed')) return ['ban', t('veto.actBan', 'Ban')];
    if (lower.includes('picked')) return ['pick', t('veto.actPick', 'Pick')];
    if (lower.includes('was left over')) return ['decider', t('veto.actKnife', 'Decider')];
    if (lower.includes('coin') || lower.includes('flip') || lower.includes('won')) return ['coin', t('veto.coin', 'Coin')];
    return null;
  };

  const steamGate = steamAuthRequired && (role === 'A' || role === 'B') && !isAuthenticated;

  // ── pieces ──
  const TeamBlock = ({ k, right }) => {
    const name = teamNames[k];
    const tc = k === 'team1' ? 't1' : 't2';
    return (
      <div className={`vt-team ${tc}${right ? ' right' : ''}${actingKey === k ? ' acting' : ''}`}>
        {teamLogos[k] ? <img className="vt-logo" src={teamLogos[k]} alt="" /> : <div className="vt-logo">{(name || '?')[0]}</div>}
        <div className="vt-team-text">
          <div className="vt-team-name">{name}</div>
          <div className="vt-team-sub">
            <span>{k === 'team1' ? t('veto.team1', 'Team 1') : t('veto.team2', 'Team 2')}</span>
            {roleTeamKey === k && <b>{t('veto.youTag', 'You')}</b>}
            {startsVetoKey === k && session.step === 0 && <b>{t('veto.startsVeto', 'Starts the veto')}</b>}
          </div>
        </div>
      </div>
    );
  };

  const ROSTER_PAGE_SIZE = 10;
  const Roster = ({ k }) => {
    const players = teamPlayers[k] || [];
    const page = rosterPage[k] || 0;
    const pages = Math.ceil(players.length / ROSTER_PAGE_SIZE);
    const visible = players.slice(page * ROSTER_PAGE_SIZE, (page + 1) * ROSTER_PAGE_SIZE);
    const vetoRole = session.team_a ? (k === 'team1' ? (session.team_a === 'team1' ? 'A' : 'B') : (session.team_a === 'team1' ? 'B' : 'A')) : null;
    const authed = vetoRole && authenticatedUsers[vetoRole] ? authenticatedUsers[vetoRole] : [];
    return (
      <div className={`vt-roster ${k === 'team1' ? 't1' : 't2'}`}>
        <h3>{teamNames[k]}</h3>
        {players.length === 0 && <div className="vt-empty">{t('veto.noRoster', 'No players on this team yet')}</div>}
        {visible.map((p, i) => (
          <div className="vt-player" key={`${p.steam_id || p.name}-${i}`}>
            {steamAuthRequired && authed.includes(p.steam_id) && <i className="auth" title={t('veto.steamOk', 'Logged in with Steam')} />}
            <span>{p.name}</span>
            <FaceitBadge faceit={p.faceit} />
          </div>
        ))}
        {pages > 1 && (
          <div className="vt-pager">
            <button onClick={() => setRosterPage(prev => ({ ...prev, [k]: page - 1 }))} disabled={page === 0} aria-label={t('common.previous', 'Previous')}>‹</button>
            <span>{page + 1}/{pages}</span>
            <button onClick={() => setRosterPage(prev => ({ ...prev, [k]: page + 1 }))} disabled={page >= pages - 1} aria-label={t('common.next', 'Next')}>›</button>
          </div>
        )}
      </div>
    );
  };

  // connect commands (after the veto, admins + teams only)
  const pwSuffix = serverInfo && serverInfo.password ? `;password ${serverInfo.password}` : '';
  const commands = serverInfo ? [
    { key: serverInfo.public_ip ? 'server_real' : 'server', label: t('veto.publicIp', 'Public IP'), cmd: `connect ${serverInfo.ip}:${serverInfo.port}${pwSuffix}` },
    ...(serverInfo.public_ip ? [{ key: 'server_lan', label: t('veto.lanIp', 'LAN IP'), cmd: `connect ${serverInfo.public_ip}:${serverInfo.port}${pwSuffix}` }] : []),
    ...(serverInfo.sdr ? [{ key: 'server_sdr', label: t('veto.sdrLabel', 'SDR Relay'), cmd: `connect ${serverInfo.sdr}${pwSuffix}` }] : []),
  ] : [];
  const statusText = (s) => (s === 'finished' ? t('veto.matchFinished', 'Match finished') : s === 'cancelled' ? t('veto.matchCancelled', 'Match cancelled') : t(`matchStatus.${s}`, s));
  const Commands = () => (
    <>
      <div className="vt-cmds">
        {commands.map(c => (
          <button key={c.key} className={`vt-cmd${copied === c.key ? ' copied' : ''}`} onClick={() => copyToClipboard(c.cmd, c.key)}>
            <small>{copied === c.key ? t('veto.copied', 'Copied') : c.label}</small>
            <code>{c.cmd}</code>
          </button>
        ))}
      </div>
      {serverInfo && serverInfo.sdr && <p>{t('veto.sdrNote', 'SDR relay - use this if you have bad routing, ping spikes, or are connecting from another region.')}</p>}
    </>
  );

  const SETUP_TOTAL = 8;
  // the server sends step N when step N STARTS (1 = avatars … 7 = warmup, 8 = all done, -1 = failed)
  const SETUP_STEPS = [
    { label: t('matchSetup.deployAvatars', 'Deploying player avatars'), sftp: true },
    { label: t('matchSetup.sendingMatch', 'Sending match to server') },
    { label: t('matchSetup.configuringLogs', 'Configuring match logs') },
    { label: t('matchSetup.enforcingNames', 'Enforcing player names'), sftp: true },
    { label: t('matchSetup.backupPrefix', 'Setting backup prefix') },
    { label: t('matchSetup.uploadingBackups', 'Uploading map backups') },
    { label: t('matchSetup.readyForWarmup', 'Ready for warmup') },
  ];
  const setupError = startMatchStep === -1;
  const setupDone = startMatchStep >= SETUP_TOTAL && !!serverInfo;
  const setupRunning = startMatchStep > 0 && !setupError && !setupDone; // also step 8 while the connect details load
  const setupAt = setupError ? setupLastStep : startMatchStep; // the running (or failed) step
  const setupPct = setupDone || startMatchStep >= SETUP_TOTAL ? 100 : Math.round((Math.max(0, setupAt - 1) / SETUP_STEPS.length) * 100);
  const stepState = (n) => {
    if (setupError && n === setupAt) return 'failed';
    if (startMatchStep >= SETUP_TOTAL || n < setupAt) return SETUP_STEPS[n - 1].sftp && !setupHasBackup ? 'skipped' : 'done';
    if (n === setupAt && !setupError) return 'active';
    return 'waiting';
  };
  const Checklist = () => (
    <ol className="vt-checklist">
      {SETUP_STEPS.map((s, i) => {
        const st = stepState(i + 1);
        return (
          <li key={i} className={st}>
            <span className="dot">
              {st === 'done' ? <Svg d={CHECK} /> : st === 'failed' ? <Svg d={CROSS} /> : st === 'active' ? <span className="spin" /> : i + 1}
            </span>
            <span className="txt">
              <b>{s.label}</b>
              {st === 'active' && setupStepLabel && setupStepLabel.replace(/\.+$/, '') !== s.label && <small>{setupStepLabel}</small>}
              {st === 'skipped' && <small>{t('matchSetup.skippedNoSftp', 'Skipped - No SFTP/FTP')}</small>}
              {st === 'failed' && <small>{setupStepLabel || t('matchSetup.errorOccurred', 'An error occurred')}</small>}
            </span>
          </li>
        );
      })}
    </ol>
  );
  const showSetupModal = !!session.finished && !serverModalDismissed && role !== 'viewer' && (startMatchStep > 0 || serverInfo);
  const showConnectBar = !!session.finished && serverInfo && role !== 'viewer' && (serverModalDismissed || startMatchStep >= 8);

  const roleChip = role === 'A' || role === 'B'
    ? { cls: colorOf(role), text: t('veto.youAre', 'You are {{team}}', { team: getTeamName(role) }) }
    : role === 'admin' ? { cls: 'admin', text: t('veto.adminView', 'Admin view') } : { cls: '', text: t('veto.spectating', 'Spectating') };

  const sideMap = sidePickMap ? mapsRaw.find(m => m.name === sidePickMap) : null;

  return (
    <div className={`vt${embedded ? ' embedded' : ''}`}>
      <div className="vt-wrap">
        <div className="vt-top">
          {session.match_id && (
            <button className="vt-back" onClick={goToMatch}>← {embedded ? t('veto.backToMatch', 'Back to the match') : t('veto.viewMatch', 'Match page')}</button>
          )}
          <span className={`vt-role ${roleChip.cls}`}><i />{roleChip.text}</span>
          {role === 'admin' && !session.finished && (
            <button className="vt-reset" onClick={handleReset} disabled={actionLoading}>{t('veto.reset', 'Reset veto')}</button>
          )}
        </div>

        <header className="vt-head">
          <TeamBlock k="team1" />
          <div className="vt-vs">
            <span>vs</span>
            <small>{session.format_label || String(session.format || '').replace(/_/g, ' ').toUpperCase()}</small>
          </div>
          <TeamBlock k="team2" right />
        </header>

        {showConnectBar && (
          <section className="vt-connect">
            <div className="vt-connect-head">
              <h2>{t('veto.serverReady', 'Server ready')}</h2>
              {matchStatus && <span className={`vt-status ${matchStatus}`}>{statusText(matchStatus)}</span>}
              <span>{t('veto.copyHint', 'Click a command to copy it, then paste it into the CS2 console.')}</span>
            </div>
            <Commands />
          </section>
        )}

        <div className="vt-now" style={headColor ? { '--nc': headColor } : undefined}>
          <h1>{headline}</h1>
          {(sub || (!session.finished && currentStep && vetoStarted)) && (
            <p>
              {!session.finished && currentStep && vetoStarted && t('veto.stepOf', 'Step {{n}} of {{total}}', { n: Math.min(session.step, sequence.length) + 1, total: sequence.length })}
              {sub && !session.finished && currentStep && vetoStarted ? '. ' : ''}{sub}
            </p>
          )}
        </div>

        {sequence.length > 0 && (
          <ol className="vt-steps" aria-label={t('veto.sequence', 'Veto steps')}>
            {sequence.map((s, i) => {
              const state = session.finished || i < session.step ? 'done' : (i === session.step && vetoStarted ? 'now' : '');
              return (
                <li key={i} className={`vt-step ${colorOf(s.t)} ${state}`} aria-current={state === 'now' ? 'step' : undefined}>
                  <em>{i + 1}</em><b>{stepName(s.a)}</b>{s.t !== 'System' && <span>{getTeamName(s.t)}</span>}
                </li>
              );
            })}
          </ol>
        )}

        {steamAuthRequired && (role === 'A' || role === 'B') && !session.finished && (
          isAuthenticated
            ? <div className="vt-note ok">✓ {t('veto.steamOk', 'Logged in with Steam')}</div>
            : (
              <div className="vt-note">
                <span>{t('veto.steamNeeded', 'Log in with Steam to take part in the veto.')}</span>
                <button className="vt-steam" onClick={handleSteamLogin} disabled={steamLoginLoading}>
                  {steamLoginLoading ? t('veto.redirecting', 'Opening Steam…') : t('veto.steamLogin', 'Log in with Steam')}
                </button>
              </div>
            )
        )}

        {error && <div className="vt-note err" role="alert">{error}</div>}

        {/* ready-up */}
        {!!session.use_coin_flip && !session.finished && session.step === 0 && (!ready.A || !ready.B) && !coinFlip && (
          <section className="vt-stage">
            <div className="vt-ready-row">
              {['A', 'B'].map(r => (
                <div key={r} className={`vt-ready ${colorOf(r)}${ready[r] ? ' on' : ''}`}>
                  <strong>{getTeamName(r)}</strong>
                  <span>{ready[r] ? t('veto.ready', 'Ready') : t('veto.notReady', 'Not ready')}</span>
                </div>
              ))}
            </div>
            {(role === 'A' || role === 'B') && !ready[role] && <button className="vt-btn" onClick={handleReady}>{t('veto.readyUp', 'Ready up')}</button>}
          </section>
        )}

        {/* coin call */}
        {!!session.use_coin_flip && !session.finished && !coinFlip && ready.A && ready.B && role === 'A' && (
          <section className="vt-stage">
            <div className="vt-coin">?</div>
            <div className="vt-btn-row">
              <button className="vt-btn" onClick={() => handleCoinCall('heads')}>{t('veto.heads', 'Heads')}</button>
              <button className="vt-btn" onClick={() => handleCoinCall('tails')}>{t('veto.tails', 'Tails')}</button>
            </div>
          </section>
        )}

        {/* coin result + who starts */}
        {!!session.use_coin_flip && !session.finished && coinFlip && coinFlip.status !== 'decided' && (coinAnimating || coinRevealed) && (
          <section className="vt-stage">
            <div className={`vt-coin${coinAnimating ? ' spin' : ''}`}>{coinAnimating ? '?' : t(`veto.${coinFlip.result}`, coinFlip.result)}</div>
            {!coinAnimating && role === coinFlip.winner && (() => {
              const team1IsWinner = (session.team_a === 'team1' && coinFlip.winner === 'A') || (session.team_a === 'team2' && coinFlip.winner === 'B');
              return (
                <div className="vt-btn-row">
                  <button className={`vt-btn${team1IsWinner ? '' : ' ghost'}`} onClick={() => handleCoinDecision(team1IsWinner ? 'first' : 'second')}>{t('veto.teamStarts', '{{team}} starts', { team: teamNames.team1 })}</button>
                  <button className={`vt-btn${team1IsWinner ? ' ghost' : ''}`} onClick={() => handleCoinDecision(team1IsWinner ? 'second' : 'first')}>{t('veto.teamStarts', '{{team}} starts', { team: teamNames.team2 })}</button>
                </div>
              );
            })()}
          </section>
        )}

        {/* the map board */}
        <section className="vt-board" aria-label={t('veto.mapPool', 'Map pool')}>
          {maps.map(m => {
            const actionable = isMyTurn && m.status === 'available' && currentStep && (currentStep.a === 'ban' || currentStep.a === 'pick');
            const canClick = actionable && (!steamAuthRequired || isAuthenticated || role === 'admin');
            const isBan = currentStep?.a === 'ban';
            const byCls = m.pickedBy ? (m.pickedBy === 'System' ? 'csys' : `c${colorOf(m.pickedBy).slice(1)}`) : '';
            const num = mapNumber[m.name];
            return (
              <article key={m.name} className={`vt-map ${m.status} ${byCls}${canClick ? ' open' : ''}`}>
                {m.image_url && <img className="bg" src={m.image_url} alt="" draggable={false} onError={imgFallback} />}
                {num && <span className="vt-map-tag">{t('veto.mapN', 'Map {{n}}', { n: num })}</span>}
                {m.workshop_id && (
                  <a className="vt-map-ws" href={`https://steamcommunity.com/sharedfiles/filedetails/?id=${m.workshop_id}`} target="_blank" rel="noopener noreferrer" title={t('veto.workshop', 'Steam Workshop page')}>Workshop</a>
                )}
                <div className="vt-map-body">
                  <div className="vt-map-name">{mapTitle(m.name)}</div>
                  {m.status === 'banned' && m.pickedBy && <div className="vt-map-who">{t('veto.bannedBy', 'Banned by')} <b>{getTeamName(m.pickedBy)}</b></div>}
                  {m.status === 'picked' && m.pickedBy && m.pickedBy !== 'System' && <div className="vt-map-who">{t('veto.pickedBy', 'Picked by')} <b>{getTeamName(m.pickedBy)}</b></div>}
                  {(m.status === 'decider' || (m.status === 'picked' && m.pickedBy === 'System')) && <div className="vt-map-who">{t('veto.decider', 'Decider')}</div>}
                  {m.side && m.sideChosenBy && (
                    <span className={`vt-map-side ${m.side.toLowerCase() === 'ct' ? 'ct' : 't'}`}>{t('veto.startsSide', '{{team}} starts {{side}}', { team: getTeamName(m.sideChosenBy), side: m.side.toUpperCase() })}</span>
                  )}
                  {canClick && (
                    <button className={`vt-map-act ${isBan ? 'ban' : 'pick'}`} onClick={() => handleAction(m.name, null)} disabled={actionLoading}>
                      {isBan ? t('veto.banMap', 'Ban {{map}}', { map: mapTitle(m.name) }) : t('veto.pickMap', 'Pick {{map}}', { map: mapTitle(m.name) })}
                    </button>
                  )}
                  {actionable && !canClick && steamGate && <div className="vt-map-lock">{t('veto.locked', 'Log in with Steam first')}</div>}
                </div>
              </article>
            );
          })}
        </section>

        <div className="vt-lower">
          <Roster k="team1" />
          <section className="vt-log">
            <div className="vt-log-head">
              <div>
                <h3>{role === 'admin' && steamAuthRequired ? t('veto.auditTitle', 'Veto log with Steam accounts') : t('veto.log', 'Veto log')}</h3>
                {role === 'admin' && steamAuthRequired && <p>{t('veto.auditHint', 'Shows which Steam account made each move. Only admins see the Steam IDs.')}</p>}
              </div>
              {logs.length > 0 && (
                <button className={`vt-copy${copied === 'log' ? ' copied' : ''}`} onClick={() => {
                  const header = `**Map Veto - ${teamNames.team1} vs ${teamNames.team2}**`;
                  copyToClipboard(`${header}\n\n${logs.map(l => logText(l) || 'No message').join('\n')}`, 'log');
                }}>{copied === 'log' ? t('veto.copied', 'Copied') : t('veto.copyLog', 'Copy log')}</button>
              )}
            </div>
            {logs.length === 0
              ? <div className="vt-empty">{t('veto.logEmpty', 'Every ban and pick appears here as it happens.')}</div>
              : (
                <ol>
                  {logs.map((log, i) => {
                    const kind = logKind(log);
                    const obj = typeof log === 'string' ? { message: log } : log;
                    return (
                      <li key={i} className={i === logs.length - 1 ? 'new' : ''}>
                        <i>{i + 1}</i>
                        {kind && <span className={`kind ${kind[0]}`}>{kind[1]}</span>}
                        <span>
                          <span className="msg">{obj.message || 'No message'}</span>
                          {role === 'admin' && steamAuthRequired && (
                            <span className="audit">
                              {obj.steamId
                                ? <>Steam ID <code>{obj.steamId}</code>{obj.team ? `, ${t('veto.teamLetter', 'team {{team}}', { team: obj.team })}` : ''}{obj.timestamp ? `, ${new Date(obj.timestamp).toLocaleTimeString()}` : ''}</>
                                : t('veto.noSteam', 'No Steam ID (system move or older entry)')}
                            </span>
                          )}
                        </span>
                      </li>
                    );
                  })}
                </ol>
              )}
          </section>
          <Roster k="team2" />
        </div>
      </div>

      {/* side pick */}
      {sidePickMap && (
        <div className="vt-modal" role="dialog" aria-modal="true">
          <div className="vt-dialog">
            {sideMap && sideMap.image_url && <div className="vt-dialog-art" style={{ backgroundImage: `url(${sideMap.image_url})` }} />}
            <div className="vt-dialog-in">
              <p>{t('veto.chooseSide', 'Choose your starting side')}</p>
              <h2>{mapTitle(sidePickMap)}</h2>
              <div className="vt-sides">
                <button className="vt-side ct" onClick={() => handleAction(null, 'ct')} disabled={actionLoading}>
                  <img src="/veto_ct.png" alt="" /><strong>CT</strong><span>{t('veto.ctName', 'Counter-Terrorist')}</span>
                </button>
                <button className="vt-side t" onClick={() => handleAction(null, 't')} disabled={actionLoading}>
                  <img src="/veto_t.png" alt="" /><strong>T</strong><span>{t('veto.tName', 'Terrorist')}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* match setup progress, then the connect commands */}
      {showSetupModal && (
        <div className="vt-modal" role="dialog" aria-modal="true">
          <div className="vt-dialog">
            <button className="vt-x" onClick={() => setServerModalDismissed(true)} aria-label={t('common.close', 'Close')}>✕</button>
            <div className="vt-dialog-in">
              {(setupRunning || setupError) && (
                <>
                  <div className={`vt-ring${setupError ? ' err' : ''}`} style={{ '--p': setupError ? 100 : setupPct }}>
                    <span>{setupError ? <Svg d={CROSS} size={26} /> : `${setupPct}%`}</span>
                  </div>
                  <h2>{setupError ? t('matchSetup.setupFailed', 'Setup Failed') : t('matchSetup.settingUp', 'Setting Up Match')}</h2>
                  <p>{setupError
                    ? t('veto.setupFailedHint', 'The match could not be sent to the server. An admin can start it again from the match page.')
                    : t('veto.stepOf', 'Step {{n}} of {{total}}', { n: Math.min(Math.max(1, startMatchStep), SETUP_STEPS.length), total: SETUP_STEPS.length })}</p>
                  <Checklist />
                </>
              )}
              {setupDone && (
                <>
                  <div className="vt-ring done" style={{ '--p': 100 }}><span><Svg d={CHECK} size={30} /></span></div>
                  <h2>{t('veto.serverReady', 'Server ready')}</h2>
                  {matchStatus && <span className={`vt-status ${matchStatus}`}>{statusText(matchStatus)}</span>}
                  <p>{t('veto.copyHint', 'Click a command to copy it, then paste it into the CS2 console.')}</p>
                  <Commands />
                  <details className="vt-setup-done">
                    <summary>{t('veto.setupStepsDone', 'All setup steps done')}</summary>
                    <Checklist />
                  </details>
                </>
              )}
              {!setupRunning && !setupError && !setupDone && !serverInfo && (
                <>
                  <div className="vt-ring" style={{ '--p': 0 }}><span>0%</span></div>
                  <h2>{t('matchSetup.settingUp', 'Setting Up Match')}</h2>
                  <RotatingStatusText />
                  <Checklist />
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
